/** @prettier */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {patchDailySchedule, putTodaySchedule} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';

/**
 * スケジュール 削除ダイアログ
 * @param props props
 * - siteID サイトID
 * - targetGroupID 削除するスケジュールのグループID
 * - scheduleIndex 削除するスケジュールのインデックス番号
 * - isOpenDialog ダイアログ表示フラグ
 * - setIsOpenDialog ダイアログ表示フラグ 設定関数
 * - setScheduleChange スケジュール更新フラグ 設定関数
 * - todayScheduleData 当日スケジュールのBodyデータ(当日スケジュール削除時のみ)
 * - type 選択している特定日（特定日スケジュール削除時のみ）
 * - dailyScheduleData 特定日スケジュールのレスポンスデータ（特定日スケジュール削除時のみ）
 * @returns JSX
 */
const ScheduleDeleteDialog = (props: {
  targetGroupID: string;
  scheduleIndex: number;
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  scheduleChangeHandler: () => void;
  todayScheduleData?: apiIF.res.getTodaySchedule['today-schedule'];
  type?: keyof apiIF.res.getDailySchedule;
  dailyScheduleData?: apiIF.res.getDailySchedule;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * 当日スケジュールのリクエストBodyを修正する
   * @param todayData {apiIF.reqParameters.putTodaySchedule} 修正前のBodyデータ
   * @returns 修正後のBodyデータ
   */
  const tempTodayScheduleDataToBodyData = (
    todayData: apiIF.res.getTodaySchedule['today-schedule']
  ) => {
    //修正前のBodyデータを初期値に設定
    let settingBodyData: apiIF.reqParameters.putTodaySchedule = {
      'today-schedule': todayData,
    };
    //引数のindexで指定されているスケジュールを削除する
    settingBodyData['today-schedule'].splice(props.scheduleIndex, 1);
    return settingBodyData;
  };

  /**
   * 特定日スケジュールのレスポンスデータを修正し、Bodyデータに変形する
   * @param response 特定日スケジュールのレスポンスデータ
   * @param dailyDataBody 特定日スケジュールのBodyデータの初期値
   * @returns 修正後の特定日スケジュールのBodyデータ
   */
  const tempDailyScheduleDataToBodyData = (
    response: apiIF.res.getDailySchedule
  ) => {
    //修正前のBodyデータを初期値に設定
    let settingBodyData: apiIF.reqParameters.patchDailySchedule = {};
    //prettier-ignore
    //特定日４つをループ
    for (const key of Object.keys(response) as (keyof apiIF.res.getDailySchedule)[]) {
      //編集する特定日タイプ以外の場合はスキップ
      if (key !== props.type) {
        continue;
      }
      //引数のindexで指定されているスケジュールを削除する
      response[key].splice(props.scheduleIndex, 1);
      settingBodyData[key] = {schedule: response[key]};
    }
    return settingBodyData;
  };

  /**
   * OKボタン押下時処理
   */
  const handleDelete = () => {
    if (siteID === undefined) return;

    setAPIWait(true);
    if (props.todayScheduleData !== undefined) {
      //当日スケジュールの修正
      //APIに渡すデータを作成
      const todayScheduleDataBody: apiIF.reqParameters.putTodaySchedule =
        tempTodayScheduleDataToBodyData(props.todayScheduleData);
      //当日スケジュールの編集
      putTodaySchedule(siteID, props.targetGroupID, todayScheduleDataBody)
        .then(response => {
          props.scheduleChangeHandler();
          handleClose();
        })
        .catch(err => {
          apiErrorAlert(trans);
        })
        .finally(() => {
          setAPIWait(false);
        });
    } else if (props.dailyScheduleData !== undefined) {
      //特定日スケジュールの修正
      //APIに渡すデータを作成
      const dailyScheduleDataBody: apiIF.reqParameters.patchDailySchedule =
        tempDailyScheduleDataToBodyData(props.dailyScheduleData);
      //特定日スケジュールの編集はCSVの型を回避するために、
      //レスポンスデータを編集してBodyデータの形にする
      patchDailySchedule(siteID, props.targetGroupID, dailyScheduleDataBody)
        .then(response => {
          props.scheduleChangeHandler();
          handleClose();
        })
        .catch(err => {
          apiErrorAlert(trans);
        })
        .finally(() => {
          setAPIWait(false);
        });
    }
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog open={props.isOpenDialog} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('ScheduleDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {trans('ScheduleDeleteDialog.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            disabled={apiWait}
            buttonText={trans('ScheduleDeleteDialog.actions.cancel')}
          />
          <PrimaryButton
            onClick={handleDelete}
            apiWait={apiWait}
            buttonText={trans('ScheduleDeleteDialog.actions.ok')}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};
export default ScheduleDeleteDialog;
